/**
 * `usePageBoundaries` — ResizeObserver/rAF/state shell that measures the
 * editor's rendered content and returns the Y positions (CSS px) where the
 * SERVER-exported PDF starts a new page, so `PageBoundaryOverlay` can draw a
 * "see the pages" indicator without the editor's DOM ever growing a real gap
 * (RECUR-27304).
 *
 * Delta v3: the actual page-start MODEL — the DOM-measuring glue
 * (`getTopLevelBlocks`, `findTableElement`, `buildAtomicUnits`, …) and the
 * pure pagination fit-loop — now lives in `../exporters/pageStartUnits.ts` +
 * `../exporters/pageBreakModel.ts`, moved out so the SAME model output can
 * also be consumed by the server exporter (`exportToPdfServer.ts`), which
 * injects forced page breaks at the model's page-starts so the exported PDF
 * obeys the model exactly (see that file). This hook is now JUST the
 * ResizeObserver/rAF/state shell (D3): find the content element, call
 * `computePageBoundaries`, and re-render only when the derived result
 * actually changes. Public signature + return shape UNCHANGED — `PlateEditor.tsx`
 * and `PageBoundaryOverlay` are untouched by this refactor.
 *
 * Measurement: a ResizeObserver on the `[data-testid="plate-editor-content"]`
 * element found inside the passed container element (the same selector both
 * PDF exporters key on) fires on every edit/import/resize-driven reflow. The
 * callback is rAF-throttled (coalesces a burst of RO notifications into one
 * measurement per frame) and only calls `setState` when the derived result
 * actually changes — a long document re-renders the overlay only when a
 * boundary moves or a page is added/removed, not on every keystroke.
 *
 * Takes the container as a plain element (not a `RefObject`) so the caller
 * holds it in state via a callback ref: `PlateErrorBoundary`'s crash-recovery
 * remount swaps in a brand-new DOM node, and only a STATE change (unlike a
 * stable ref object, whose identity never changes) makes this hook's effect
 * re-run and re-attach the observer to the new node instead of staying
 * attached to the now-detached one.
 *
 * `enabled=false` (the default every non-document PlateEditor surface uses)
 * short-circuits to an empty result and never creates an observer.
 *
 * RECUR-27304 gap-display: this hook is now also the SOLE place that WRITES
 * the page-gap styling onto the live DOM (`applyPageGapStyles`, below) — see
 * `pageStartUnits.ts`'s module doc for the normalized/live coordinate-space
 * split this depends on. Applied as a CSS custom property + class only,
 * never a Slate transform, and re-applied on EVERY measurement pass (not
 * gated behind the "did the boundaries change" check) so a Plate/React
 * re-render that drops the inline style/class self-heals on the very next
 * pass instead of staying wrong until some unrelated future edit happens to
 * re-trigger a measurement.
 */
import { useEffect, useRef, useState } from 'react';
import plateEditorStyles from '../PlateEditor.module.scss';
import {
    computePageBoundaries,
    computePageGapTargets,
    getTopLevelBlocks,
    EMPTY_BOUNDARIES,
    PAGE_GAP_CSS_VAR,
    type PageBoundaries,
    type PageGapTarget,
} from '../exporters/pageStartUnits';

export type { PageBoundaries };
export { computePageBoundaries };

const CONTENT_SELECTOR = '[data-testid="plate-editor-content"]';

/**
 * Renders each target's gap as REAL vertical space directly on its live
 * block (RECUR-27304 gap-display) — margin-top driven by a CSS custom
 * property + class (see `PlateEditor.module.scss`'s `.pageGapBefore`), never
 * a Slate transform and never an inserted DOM node, so the document
 * value / save-safety guarantee is untouched. Every top-level block in
 * `allBlocks` that ISN'T a current target, OR whose `gapPx` is exactly 0 (a
 * page that filled to capacity, nothing to carry forward), has its property +
 * class removed — so a block that stops being a page start (or never had any
 * leftover space to begin with) never carries a stale/empty gap.
 *
 * RECUR-27304 "one consistent marker": `--page-gap` now carries the RAW
 * `gapPx` alone — the constant gutter this used to add on top of every
 * target (even a zero-gap one) was removed, since the editor no longer draws
 * any band/label there (see `PageBoundaryOverlay`). `getInjectedGapPx`'s
 * normalization (pageStartUnits.ts) keeps subtracting back out exactly
 * whatever total is currently painted, so idempotency is unaffected by this
 * change — it was already generic over "however much is currently painted".
 */
function applyPageGapStyles(allBlocks: HTMLElement[], targets: PageGapTarget[]): void {
    const gapByElement = new Map(targets.map((target) => [target.element, target.gapPx] as const));
    allBlocks.forEach((block) => {
        const gapPx = gapByElement.get(block);
        if (gapPx !== undefined && gapPx > 0) {
            block.style.setProperty(PAGE_GAP_CSS_VAR, `${gapPx}px`);
            block.classList.add(plateEditorStyles.pageGapBefore);
        } else {
            block.style.removeProperty(PAGE_GAP_CSS_VAR);
            block.classList.remove(plateEditorStyles.pageGapBefore);
        }
    });
}

export function usePageBoundaries(containerEl: HTMLElement | null, enabled: boolean): PageBoundaries {
    const [state, setState] = useState<PageBoundaries>(EMPTY_BOUNDARIES);
    const lastKeyRef = useRef('');

    // Shared by every early-out below (RECUR-27304 corrective): resets BOTH
    // the guard key and the returned state, so a remount that finds nothing
    // to measure never leaves a PRIOR editor instance's boundaries showing.
    const resetToEmpty = () => {
        lastKeyRef.current = '';
        setState(EMPTY_BOUNDARIES);
    };

    useEffect(() => {
        if (!enabled) {
            resetToEmpty();
            return;
        }

        const contentEl = containerEl?.querySelector<HTMLElement>(CONTENT_SELECTOR);
        // Defensive: jsdom/older browsers may not implement ResizeObserver at
        // all (mirrors the same guard in ProgressBorder.tsx). Reset (not a
        // bare early-out) — `PlateErrorBoundary`'s crash-recovery remount
        // swaps in a brand-new content-wrap element (see this file's module
        // doc), and if ITS content isn't found here, a stale seam from the
        // OLD instance must not linger on screen.
        if (!contentEl || typeof ResizeObserver === 'undefined') {
            resetToEmpty();
            return;
        }

        let rafId: number | null = null;
        const measure = () => {
            rafId = null;
            const blocks = getTopLevelBlocks(contentEl);
            const width = contentEl.offsetWidth;
            const next = computePageBoundaries(blocks, width);
            // Always re-applied (RECUR-27304 gap-display) — see this file's
            // module doc for why this sits outside the "did it change" guard
            // below. A gap value that just changed won't be reflected in
            // THIS pass's own `next.boundaries` yet (they were read before
            // this line ran) — the DOM mutation below shifts layout, which
            // re-triggers the observer, and the very next pass reports the
            // settled position. That one-frame lag is the accepted tradeoff
            // for never touching Slate content to render this space.
            applyPageGapStyles(blocks, computePageGapTargets(blocks, width));
            // The paginator-accurate model's boundaries aren't an arithmetic
            // progression (Delta v2) — an edit can shift every boundary Y
            // after it without changing the total COUNT, so the guard key
            // must cover the actual positions, not just `count:pageHeightPx`.
            // `kinds` is included too (RECUR-27304 Word-frame visuals) — a
            // boundary can in principle swap kind (e.g. 'row' <-> 'block')
            // while its Y and gap coincidentally stay the same, and the
            // overlay renders each kind differently.
            const nextKey = `${next.pageHeightPx}:${next.boundaries.join(',')}:${next.gaps.join(',')}:${next.kinds.join(',')}`;
            if (nextKey === lastKeyRef.current) return;
            lastKeyRef.current = nextKey;
            setState(next);
        };

        // Coalesces a burst of notifications — from EITHER observer below,
        // possibly both in the same frame (e.g. an edit that both reflows
        // text AND resizes the container) — into a single measurement per
        // animation frame.
        const scheduleMeasure = () => {
            if (rafId != null) return;
            rafId = requestAnimationFrame(measure);
        };

        const observer = new ResizeObserver(scheduleMeasure);
        observer.observe(contentEl);

        // RECUR-27304 corrective: ResizeObserver only fires on a content-BOX
        // size change — an edit that shifts where a page breaks WITHOUT
        // changing the container's own box (e.g. text reflowing as
        // characters are typed/deleted inside an unchanged-height paragraph)
        // never notifies it, leaving the overlay stale until some LATER,
        // size-changing edit happens to re-trigger it. A MutationObserver on
        // the same content root catches those too, feeding the SAME
        // rAF-coalesced `measure` path.
        //
        // Scoped to ONLY `{ childList, subtree, characterData }` — NEVER
        // `attributes`: `applyPageGapStyles` (above) writes the `--page-gap`
        // style property + `pageGapBefore` class onto a block on EVERY
        // measurement pass, so observing attribute mutations would make this
        // observer notify itself on its own write, forever (infinite
        // self-triggering loop). Structural/text edits only.
        const mutationObserver = new MutationObserver(scheduleMeasure);
        mutationObserver.observe(contentEl, { childList: true, subtree: true, characterData: true });

        measure();

        return () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [containerEl, enabled]);

    return state;
}
